import { InlineCode } from "@leafygreen-ui/typography";
import { Link } from "react-router-dom";
import { StyledRouterLink } from "@evg-ui/lib/components/styles";
import { shortenGithash } from "@evg-ui/lib/utils/string";
import { useTaskAnalytics } from "analytics";
import { CopyableID } from "components/CopyableID";
import { MetadataItem, MetadataSection } from "components/MetadataCard";
import { getProjectPatchesRoute, getTaskRoute } from "constants/routes";
import { TaskQuery } from "gql/generated/types";
import { CompletedBy } from "./CompletedBy";
import { TaskOwnership } from "./TaskOwnership";

type Task = NonNullable<TaskQuery["task"]>;

interface GeneralSectionProps {
  task: Task;
}

export const GeneralSection: React.FC<GeneralSectionProps> = ({ task }) => {
  const taskAnalytics = useTaskAnalytics();

  const {
    baseTask,
    completedBy,
    displayTask,
    execution,
    generatedBy,
    generatedByName,
    project,
    versionMetadata,
  } = task;

  const {
    id: projectID,
    identifier: projectIdentifier,
    owner,
    repo,
  } = project || {};
  const baseCommit = shortenGithash(
    baseTask?.versionMetadata?.revision ?? undefined,
  );
  const { isPatch, user } = versionMetadata ?? {};

  return (
    <MetadataSection>
      <CopyableID textToCopy={task.id} tooltipLabel="Copy task ID" />
      <MetadataItem data-testid="task-metadata-project" label="Project">
        <StyledRouterLink
          data-testid="project-link"
          onClick={() =>
            taskAnalytics.sendEvent({
              name: "Clicked metadata link",
              "link.type": "project link",
            })
          }
          to={getProjectPatchesRoute(projectIdentifier || projectID || "")}
        >
          {projectIdentifier || `${owner}/${repo}`}
        </StyledRouterLink>
      </MetadataItem>
      <MetadataItem label="Submitted by">{user.userId}</MetadataItem>
      {generatedBy && (
        <MetadataItem label="Generated by">
          <StyledRouterLink to={getTaskRoute(generatedBy)}>
            {generatedByName}
          </StyledRouterLink>
        </MetadataItem>
      )}
      {completedBy && <CompletedBy completedBy={completedBy} />}
      <TaskOwnership execution={execution} taskId={task.id} />
      {displayTask && (
        <MetadataItem label="Display task">
          <StyledRouterLink
            onClick={() =>
              taskAnalytics.sendEvent({
                name: "Clicked metadata link",
                "link.type": "display task link",
              })
            }
            to={getTaskRoute(displayTask.id, {
              execution: displayTask.execution,
            })}
          >
            {displayTask.displayName}
          </StyledRouterLink>
        </MetadataItem>
      )}
      {baseTask && (
        <MetadataItem label={isPatch ? "Base commit" : "Previous commit"}>
          <InlineCode
            as={Link}
            onClick={() =>
              taskAnalytics.sendEvent({
                name: "Clicked metadata link",
                "link.type": "base commit",
              })
            }
            to={getTaskRoute(baseTask.id, { execution: baseTask.execution })}
          >
            {baseCommit}
          </InlineCode>
        </MetadataItem>
      )}
    </MetadataSection>
  );
};
